import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface StorageRecord {
  id: string;
  product: string;
  code: string;
  weight: number;
  status: string;
  storeDate: string;
  location: string;
  createdAt: string;
}

export default function StorageRecordsPage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<StorageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<'all' | 'stored' | 'other'>('all');

  const fetchRecords = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) {
        setRecords([]);
        setLoading(false);
        return;
      }

      const { data, error: dbError } = await supabase
        .from('gold_assets')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (dbError) throw dbError;

      setRecords(
        (data || []).map((row) => ({
          id: row.id,
          product: row.product || 'AU 999.9 投资金条',
          code: row.code || '',
          weight: Number(row.weight) || 0,
          status: row.status || '存储中',
          storeDate: row.store_date || '',
          location: row.location || '北京金库',
          createdAt: row.created_at || '',
        })),
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const filteredRecords = records.filter((r) => {
    if (filter === 'all') return true;
    if (filter === 'stored') return r.status === '存储中';
    return r.status !== '存储中';
  });

  const totalWeight = records.reduce((sum, r) => sum + r.weight, 0);
  const storedWeight = records
    .filter((r) => r.status === '存储中')
    .reduce((sum, r) => sum + r.weight, 0);

  const statusBadge = (status: string) => {
    if (status === '存储中') {
      return (
        <span className="bg-gold-100 text-gold-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
          {status}
        </span>
      );
    }
    if (status === '已提取') {
      return (
        <span className="bg-green-50 text-green-600 text-[10px] px-2 py-0.5 rounded-full font-medium">
          {status}
        </span>
      );
    }
    if (status === '已回购') {
      return (
        <span className="bg-red-50 text-red-500 text-[10px] px-2 py-0.5 rounded-full font-medium">
          {status}
        </span>
      );
    }
    return (
      <span className="bg-gray-100 text-gray-500 text-[10px] px-2 py-0.5 rounded-full font-medium">
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-brown-50 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <h1 className="text-gold-400 text-sm font-bold">寄存记录</h1>
        <div className="w-8" />
      </div>

      {/* Summary Card */}
      <div className="px-4 mt-4">
        <div className="bg-gradient-to-br from-brown-900 via-brown-800 to-brown-950 rounded-2xl p-5 text-white relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full border border-gold-400/20" />
          <div className="absolute bottom-0 left-1/3 w-24 h-24 rounded-full border border-gold-400/10" />
          <div className="relative">
            <p className="text-gold-300 text-xs mb-3 flex items-center gap-1.5">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-safe-2-line"></i>
              </span>
              黄金寄存总览
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-white text-3xl font-bold">
                  {totalWeight}<span className="text-base font-normal text-gold-300">g</span>
                </p>
                <p className="text-gold-300/70 text-xs mt-1">累计寄存黄金</p>
              </div>
              <div className="text-right">
                <p className="text-gold-400 text-2xl font-bold">
                  {storedWeight}<span className="text-sm font-normal">g</span>
                </p>
                <p className="text-gold-300/70 text-xs mt-1">当前存储中</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl p-1 flex gap-1">
          {[
            { key: 'all' as const, label: '全部' },
            { key: 'stored' as const, label: '存储中' },
            { key: 'other' as const, label: '已处置' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex-1 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all ${
                filter === tab.key
                  ? 'bg-gold-500 text-brown-950'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Records List */}
      <div className="px-4 mt-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <p className="text-brown-500 text-sm mb-3">寄存记录加载失败</p>
            <button
              onClick={fetchRecords}
              className="px-4 py-2 rounded-full bg-gold-500 text-brown-950 text-xs font-medium cursor-pointer whitespace-nowrap"
            >
              重新加载
            </button>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold-50 flex items-center justify-center">
              <i className="ri-safe-2-line text-gold-300 text-2xl"></i>
            </div>
            <p className="text-brown-400 text-sm">暂无寄存记录</p>
            <button
              onClick={() => navigate('/orders/perform')}
              className="mt-4 px-5 py-2 rounded-full bg-gold-500 text-brown-950 text-xs font-semibold cursor-pointer whitespace-nowrap"
            >
              去寄存黄金
            </button>
          </div>
        ) : (
          filteredRecords.map((record) => (
            <div
              key={record.id}
              className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center flex-shrink-0">
                <span className="w-6 h-6 flex items-center justify-center text-brown-950">
                  <i className="ri-vip-diamond-fill text-lg"></i>
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-brown-900 text-sm font-semibold truncate">{record.product}</h3>
                  {statusBadge(record.status)}
                </div>
                <p className="text-brown-400 text-xs mt-1">{record.weight}g · {record.code}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-brown-400 text-[10px]">
                    <i className="ri-map-pin-line mr-0.5"></i>
                    {record.location}
                  </span>
                  <span className="text-gray-300 text-[10px]">·</span>
                  <span className="text-brown-400 text-[10px]">入库 {record.storeDate}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-brown-900 text-sm font-bold">{record.weight}g</p>
                <p className="text-gold-600 text-[10px] mt-0.5">AU999.9</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom spacing */}
      <div className="h-6" />
    </div>
  );
}